import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { workPlannerApi } from "@/services/workPlannerApi";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RECURRENCE_TYPES = [
  { value: "one_time", label: "One Time" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];
const WEEKDAYS = [
  { value: 0, label: "Sun" }, { value: 1, label: "Mon" }, { value: 2, label: "Tue" },
  { value: 3, label: "Wed" }, { value: 4, label: "Thu" }, { value: 5, label: "Fri" }, { value: 6, label: "Sat" },
];
const PRIORITIES = ["low", "medium", "high", "urgent"];

const emptyActivity = () => ({
  title: "", description: "", priority: "medium", assigned_users: [],
  estimated_hours: "", start_date: "", due_date: "", preferred_time: "",
  notes: "", recurrence: { type: "one_time", days: [] },
});

export default function CreateMonthlyPlan() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [members, setMembers] = useState([]);
  const [clientId, setClientId] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [service, setService] = useState("");
  const [accountManagerId, setAccountManagerId] = useState("");
  const [assignedTeam, setAssignedTeam] = useState([]);
  const [activities, setActivities] = useState([emptyActivity()]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/clients`, { credentials: "include" })
      .then((r) => r.json()).then((d) => setClients(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${BACKEND_URL}/api/team/members`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => setMembers(Array.isArray(d) ? d.filter((m) => m.status === "approved") : []))
      .catch(() => {});
  }, []);

  const updateActivity = (idx, patch) => {
    setActivities((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };
  const toggleWeekday = (idx, day) => {
    setActivities((prev) => prev.map((a, i) => {
      if (i !== idx) return a;
      const days = a.recurrence.days.includes(day) ? a.recurrence.days.filter((d) => d !== day) : [...a.recurrence.days, day];
      return { ...a, recurrence: { ...a.recurrence, days } };
    }));
  };

  const handleSubmit = async () => {
    if (!clientId) return toast.error("Select a client");
    if (activities.some((a) => !a.title)) return toast.error("Every activity needs a title");
    setSubmitting(true);
    try {
      const result = await workPlannerApi.createPlan({
        client_id: clientId, year, month, service, account_manager_id: accountManagerId,
        assigned_team: assignedTeam, activities,
      });
      toast.success(`Plan created — ${result.tasks_created} activities scheduled`);
      navigate(`/work-planner/client/${clientId}?year=${year}&month=${month}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <h1 className="text-xl font-semibold">Create Monthly Work Plan</h1>

      <div className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 p-4">
        <div>
          <label className="text-sm font-medium">Client</label>
          <select data-testid="wp-client" value={clientId} onChange={(e) => setClientId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option value="">Select client…</option>
            {clients.map((c) => <option key={c.client_id || c._id} value={c.client_id || c._id}>{c.client_name || c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Service</label>
          <input data-testid="wp-service" value={service} onChange={(e) => setService(e.target.value)}
            placeholder="e.g. Social Media Management" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium">Month</label>
          <select data-testid="wp-month" value={month} onChange={(e) => setMonth(Number(e.target.value))}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString("default", { month: "long" })}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Year</label>
          <input data-testid="wp-year" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium">Account Manager</label>
          <select data-testid="wp-account-manager" value={accountManagerId} onChange={(e) => setAccountManagerId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option value="">None</option>
            {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">Assigned Team (default assignees)</label>
          <select data-testid="wp-assigned-team" multiple value={assignedTeam}
            onChange={(e) => setAssignedTeam(Array.from(e.target.selectedOptions, (o) => o.value))}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm h-24">
            {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">Activities</h2>
          <Button variant="outline" onClick={() => setActivities((prev) => [...prev, emptyActivity()])}>+ Add Activity</Button>
        </div>
        {activities.map((a, idx) => (
          <div key={idx} className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid={`wp-activity-${idx}`}>
            <div className="flex justify-between">
              <input value={a.title} onChange={(e) => updateActivity(idx, { title: e.target.value })}
                placeholder="Activity name (e.g. Social Media Post)" data-testid={`wp-activity-title-${idx}`}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium" />
              {activities.length > 1 && (
                <button onClick={() => setActivities((prev) => prev.filter((_, i) => i !== idx))} className="ml-2 text-red-500 text-sm">Remove</button>
              )}
            </div>
            <textarea value={a.description} onChange={(e) => updateActivity(idx, { description: e.target.value })}
              placeholder="Description" rows={2} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            <div className="grid grid-cols-4 gap-3">
              <select value={a.priority} onChange={(e) => updateActivity(idx, { priority: e.target.value })}
                className="rounded-lg border border-slate-300 px-2 py-2 text-sm">
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <input type="number" placeholder="Est. hours" value={a.estimated_hours}
                onChange={(e) => updateActivity(idx, { estimated_hours: e.target.value })}
                className="rounded-lg border border-slate-300 px-2 py-2 text-sm" />
              <input type="time" value={a.preferred_time} onChange={(e) => updateActivity(idx, { preferred_time: e.target.value })}
                className="rounded-lg border border-slate-300 px-2 py-2 text-sm" />
              <select value={a.recurrence.type} onChange={(e) => updateActivity(idx, { recurrence: { ...a.recurrence, type: e.target.value } })}
                data-testid={`wp-recurrence-${idx}`} className="rounded-lg border border-slate-300 px-2 py-2 text-sm">
                {RECURRENCE_TYPES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            {a.recurrence.type === "weekly" && (
              <div className="flex gap-2">
                {WEEKDAYS.map((d) => (
                  <button key={d.value} type="button" onClick={() => toggleWeekday(idx, d.value)}
                    data-testid={`wp-weekday-${idx}-${d.value}`}
                    className={`rounded-full px-3 py-1 text-xs ${a.recurrence.days.includes(d.value) ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                    {d.label}
                  </button>
                ))}
              </div>
            )}
            {a.recurrence.type === "one_time" && (
              <div className="grid grid-cols-2 gap-3">
                <input type="date" value={a.start_date} onChange={(e) => updateActivity(idx, { start_date: e.target.value, due_date: e.target.value })}
                  className="rounded-lg border border-slate-300 px-2 py-2 text-sm" />
              </div>
            )}
            <select multiple value={a.assigned_users} onChange={(e) => updateActivity(idx, { assigned_users: Array.from(e.target.selectedOptions, (o) => o.value) })}
              className="w-full rounded-lg border border-slate-300 px-2 py-2 text-sm h-16" data-testid={`wp-activity-assignees-${idx}`}>
              {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
            </select>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
        <Button data-testid="wp-submit" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Creating…" : "Create Monthly Plan"}
        </Button>
      </div>
    </div>
  );
}
